import {
  activateFlowDefinition,
  activateQuestionnaireDefinition,
  deactivateFlowDefinition,
  deactivateQuestionnaireDefinition,
  type FlowDefinitionDto,
  type QuestionnaireDefinitionDto,
  useFlowDefinitions,
  useQuestionnaireDefinitions,
} from "@repo/data-store";
import { EmptyState } from "@repo/ui/components/empty-state";
import { MarkdownContent } from "@repo/ui/components/markdown-content";
import { createFileRoute, Link } from "@tanstack/react-router";
import { FileQuestion, Plus } from "lucide-react";
import { renderQuestionnaireCells } from "@/features/catalog";
import { CATALOG_STATUS_OPTIONS } from "@/features/catalog/lib/catalog-status-options";
import { flowDefinitionRouteId } from "@/features/catalog/lib/flow-definition-route";
import { questionnaireDefinitionRouteId } from "@/features/catalog/lib/questionnaire-definition-route";
import { ActionButton } from "@/shared/common/action-button";
import { GroupedList, type GroupedListGroup } from "@/shared/common/grouped-list";
import { SearchFilterBar } from "@/shared/common/search-filter-bar";
import { formatDate } from "@/shared/lib/date-formats";
import { StatusPickerBadge } from "@/shared/status/status-picker-badge";

export const Route = createFileRoute("/catalog/_layout/questionnaires")({
  component: QuestionnairesComponent,
  validateSearch: (search: Record<string, unknown>): { q?: string; status?: string; group?: string } => ({
    q: (search.q as string) || undefined,
    status: (search.status as string) || undefined,
    group: (search.group as string) || undefined,
  }),
});

/** A form or a flow between forms: the two kinds this tab lists, each in its own group. */
type Row =
  | { kind: "questionnaire"; key: string; questionnaire: QuestionnaireDefinitionDto }
  | { kind: "flow"; key: string; flow: FlowDefinitionDto };

function QuestionnairesComponent() {
  const { q: search = "", status: statusFilter, group: focusGroup } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { permissions } = Route.useRouteContext();
  const setSearch = (value: string) =>
    navigate({ search: (prev) => ({ ...prev, q: value || undefined }), replace: true });
  const setStatusFilter = (value: string | null) =>
    navigate({ search: (prev) => ({ ...prev, status: value || undefined }), replace: true });
  const canCreate = permissions.canWrite("Questionnaire");
  const canCreateFlow = permissions.canWrite("PlanDefinition");
  const availability = statusFilter ? (statusFilter === "active" ? "Active" : "Inactive") : undefined;

  const questionnaires = useQuestionnaireDefinitions({
    ...(availability ? { availability } : {}),
    ...(search ? { search } : {}),
    orderBy: "mintedAt",
    orderDirection: "desc",
  });
  // The flow view has no filters of its own; a handful of rows filter here.
  const query = search.trim().toLowerCase();
  const flows = useFlowDefinitions().filter(
    (flow) =>
      (!availability || flow.availability === availability) &&
      (!query || flow.name.toLowerCase().includes(query) || flow.description?.toLowerCase().includes(query)),
  );

  const handleQuestionnaireStatus = async (questionnaire: QuestionnaireDefinitionDto, status: string) => {
    try {
      const action = status === "active" ? activateQuestionnaireDefinition : deactivateQuestionnaireDefinition;
      await action({ definitionUrl: questionnaire.url }).isPersisted.promise;
    } catch {
      // Domain errors surface globally via the tRPC error link.
    }
  };

  const handleFlowStatus = async (flow: FlowDefinitionDto, status: string) => {
    try {
      const action = status === "active" ? activateFlowDefinition : deactivateFlowDefinition;
      await action({ definitionUrl: flow.url });
    } catch {
      // Domain errors surface globally via the tRPC error link.
    }
  };

  const addButton = (allowed: boolean, to: "/catalog/questionnaires/new" | "/catalog/flows/new") => () => (
    <ActionButton state={{ allowed }} variant="ghost" size="icon-xs" to={to}>
      <Plus className="size-4" />
    </ActionButton>
  );

  const groups: GroupedListGroup<Row>[] = [
    {
      id: "questionnaires",
      name: "Questionnaires",
      items: questionnaires.map((questionnaire) => ({ kind: "questionnaire", key: questionnaire.url, questionnaire })),
      headerAction: addButton(canCreate, "/catalog/questionnaires/new"),
    },
    {
      id: "flows",
      name: "Flows",
      items: flows.map((flow) => ({ kind: "flow", key: flow.url, flow })),
      headerAction: addButton(canCreateFlow, "/catalog/flows/new"),
    },
  ];

  return (
    <GroupedList
      focusGroup={focusGroup}
      groups={groups}
      isLoading={false}
      header={
        <SearchFilterBar
          search={search}
          onSearchChange={setSearch}
          placeholder="Search questionnaires and flows..."
          statusOptions={[
            { value: "active", label: "Active" },
            { value: "retired", label: "Inactive" },
          ]}
          statusValue={statusFilter ?? null}
          onStatusChange={setStatusFilter}
        />
      }
      columns={[
        { id: "title", width: "1fr" },
        { id: "date", width: "auto" },
        { id: "status", width: "100px" },
      ]}
      columnGap="4px"
      renderCells={(row) =>
        row.kind === "questionnaire"
          ? renderQuestionnaireCells(row.questionnaire, handleQuestionnaireStatus)
          : renderFlowCells(row.flow, handleFlowStatus)
      }
      wrapItem={(row, children) =>
        row.kind === "questionnaire" ? (
          <Link
            to="/catalog/questionnaires/$questionnaireId"
            params={{ questionnaireId: questionnaireDefinitionRouteId(row.questionnaire.url) }}
            className="cursor-pointer"
          >
            {children}
          </Link>
        ) : (
          <Link
            to="/catalog/flows/$flowId"
            params={{ flowId: flowDefinitionRouteId(row.flow.url) }}
            className="cursor-pointer"
          >
            {children}
          </Link>
        )
      }
      renderItem={() => null}
      getItemKey={(row) => row.key}
      emptyMessage="No questionnaires found"
      isFiltered={!!search || !!statusFilter}
      emptyState={
        <EmptyState className="m-auto">
          <EmptyState.Icon>
            <FileQuestion className="size-4" />
          </EmptyState.Icon>
          <EmptyState.Content>
            <EmptyState.Title>No questionnaires yet</EmptyState.Title>
            <EmptyState.Description>
              Create a questionnaire to collect structured responses from patients.
            </EmptyState.Description>
          </EmptyState.Content>
          {canCreate && (
            <EmptyState.Actions>
              <ActionButton state={{ allowed: canCreate }} size="sm" to="/catalog/questionnaires/new">
                <Plus className="size-4 mr-1" />
                New questionnaire
              </ActionButton>
            </EmptyState.Actions>
          )}
        </EmptyState>
      }
    />
  );
}

function renderFlowCells(flow: FlowDefinitionDto, onStatusChange: (flow: FlowDefinitionDto, status: string) => void) {
  const steps = flow.nodes.length;
  return [
    <div key="title" className="flex flex-col gap-0.5 min-w-0">
      <span className="font-medium text-sm">{flow.name}</span>
      {flow.description ? (
        <MarkdownContent className="text-xs text-muted-foreground line-clamp-1">{flow.description}</MarkdownContent>
      ) : (
        <span className="text-xs text-muted-foreground">
          {steps} questionnaire{steps === 1 ? "" : "s"}
        </span>
      )}
    </div>,
    flow.mintedAt ? (
      <span key="date" className="text-xs text-muted-foreground text-right">
        {formatDate(flow.mintedAt)}
      </span>
    ) : null,
    /* biome-ignore lint/a11y/noStaticElementInteractions: stop propagation to parent link */
    <div
      key="status"
      className="flex justify-end"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <StatusPickerBadge
        value={flow.availability === "Active" ? "active" : "retired"}
        options={CATALOG_STATUS_OPTIONS}
        onChange={(status) => onStatusChange(flow, status)}
      />
    </div>,
  ];
}
